import React from "react";
import ZestComponent from "./ZestComponent";
import type { IconLibrary, IconSize } from "./types";

const LIBRARIES: Record<IconLibrary, string> = {
  standard: "fa-regular fa-sharp",
  victory: "fak",
};

const SIZES: Record<IconSize, string> = {
  sm: "text-sm leading-4",
  md: "text-base leading-4",
  lg: "text-lg leading-6",
  xl: "text-xl leading-8",
};

export interface IconProps {
  icon: string;
  library?: IconLibrary;
  size?: IconSize;
  classes?: string;
  label?: string | null;
}

// Decorative by default (aria-hidden) since an icon rarely carries meaning on
// its own — pass `label` when it does, e.g. an icon-only button, to expose it
// as an accessible image instead. Typed as a literal union rather than
// Record<string, string> so it satisfies ZestComponent's DOM-attribute props.
type AccessibilityProps = { role: "img"; "aria-label": string } | { "aria-hidden": "true" };

const accessibilityProps = (label: string | null): AccessibilityProps =>
  label ? { role: "img", "aria-label": label } : { "aria-hidden": "true" };

// Defensive `?? default` fallbacks kept for parity with untyped JS callers
// (and required anyway under noUncheckedIndexedAccess); the unions stop TS
// callers passing anything outside the maps.
const Icon = ({ icon, library = "standard", size = "md", classes = "", label = null }: IconProps): JSX.Element => (
  <ZestComponent
    as="i"
    name="icon"
    className={[LIBRARIES[library] ?? LIBRARIES.standard, `fa-${icon}`, SIZES[size] ?? SIZES.md, classes].join(" ")}
    variants={{ icon, library }}
    {...accessibilityProps(label)}
  />
);

export default Icon;
